import { useLazyQuery, useMutation } from '@apollo/client';
import { LeftOutlined } from '@ant-design/icons';
import { Alert, Button, Drawer, message } from 'antd';
import dayjs from 'dayjs';
import React, { useMemo, useState } from 'react';
import {
  SORT,
  BULK_ACTION_KEYS,
  BULK_ACTION_ENTITY_TOKEN,
  BULK_TAG_ENTITY,
  BULK_TAG_ENTITY_NOUNS,
  ALL_BULK_ACTIONS,
  ASSIGN_TAG_APPLICATION_TYPE,
  DYNAMIC_CONFIG_TYPES,
  TAGS_CONFIG_SUBTYPES,
} from '../../common/constants';
import {
  DYNAMIC_CONFIG_ITEMS,
  TOY_RESERVATION_RULES,
} from '../../modules/configs/modules/graphql/Queries';
import { ATTACH_TOY_RESERVATION_RULE } from '../../modules/configs/modules/graphql/Mutations';
import { BULK_UPDATE_USER_TAGS } from '../../modules/users/graphql/Mutations';
import { BULK_UPDATE_ORDER_TAGS } from '../../modules/orders/modules/all/graphql/Mutations';
import { BULK_UPDATE_LIBRARY_INVENTORY_TAGS } from '../../modules/lists/modules/library/graphql/Mutations';
import ActionCardsGrid from './ActionCardsGrid';
import AssignTagDrawerContent from './AssignTagDrawerContent';
import AssignSimpleTagDrawerContent from './AssignSimpleTagDrawerContent';
import RemoveTagDrawerContent from './RemoveTagDrawerContent';
import AssignReservationRuleDrawerContent from './AssignReservationRuleDrawerContent';
import RemoveReservationRuleDrawerContent from './RemoveReservationRuleDrawerContent';
import {
  buildAddRulesInput,
  buildRemoveRulesInput,
  emptyRuleGroups,
  isRuleGroupsInvalid,
} from '../ReservationRuleGroups';

const RULE_OPTIONS_LIMIT = 200;

const BulkActionButton = ({
  selectedItemIds = [],
  actions = [],
  className = '',
  label = 'Bulk Actions',
  drawerTitle = 'Bulk Actions',
  scope,
  selectedTagsForRemove = [],
  selectedRulesForRemove = [],
  tagConfigSubtype = TAGS_CONFIG_SUBTYPES?.USERS?.value,
  useOrderTagsApi = false,
  tagEntity,
  entityNoun,
  orderTagTargetField = 'orders',
  onSuccess,
  onClearSelection,
  onActionClick,
  supportsNestedSubmit,
}) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [activeActionKey, setActiveActionKey] = useState(null);
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [assignTagOptions, setAssignTagOptions] = useState([]);
  const [assignTagApplicationType, setAssignTagApplicationType] = useState(
    ASSIGN_TAG_APPLICATION_TYPE.NUMBER_OF_ORDERS,
  );
  const [numberOfOrders, setNumberOfOrders] = useState(null);
  const [tagExpiryDate, setTagExpiryDate] = useState(null);
  const [numberOfOrdersError, setNumberOfOrdersError] = useState('');
  const [tagExpiryDateError, setTagExpiryDateError] = useState('');
  const [ruleGroups, setRuleGroups] = useState(emptyRuleGroups());
  const [selectedRemoveRuleIds, setSelectedRemoveRuleIds] = useState([]);
  const [rulesByType, setRulesByType] = useState({});
  const [isRulesLoading, setIsRulesLoading] = useState(false);

  const selectedCount = selectedItemIds?.length || 0;

  const resolvedTagEntity =
    tagEntity ||
    (useOrderTagsApi ? BULK_TAG_ENTITY.ORDERS : BULK_TAG_ENTITY.USERS);

  const isSimpleTagFlow = resolvedTagEntity !== BULK_TAG_ENTITY.USERS;

  const resolvedEntityNoun =
    entityNoun || BULK_TAG_ENTITY_NOUNS?.[resolvedTagEntity] || 'items';

  const isAssignFormInvalid = useMemo(() => {
    if (!selectedTagIds?.length) return true;
    if (
      assignTagApplicationType === ASSIGN_TAG_APPLICATION_TYPE.NUMBER_OF_ORDERS
    ) {
      if (numberOfOrders === null || numberOfOrders === undefined) return true;
      const n = Number(numberOfOrders);
      return !Number.isInteger(n) || n <= 0;
    }
    if (!tagExpiryDate || !dayjs.isDayjs(tagExpiryDate)) return true;
    if (tagExpiryDate.isBefore(dayjs(), 'day')) return true;
    return false;
  }, [
    selectedTagIds,
    assignTagApplicationType,
    numberOfOrders,
    tagExpiryDate,
  ]);

  const isRuleFormInvalid = useMemo(
    () => isRuleGroupsInvalid(ruleGroups),
    [ruleGroups],
  );

  const nestedSubmitDisabled = useMemo(() => {
    switch (activeActionKey) {
      case BULK_ACTION_KEYS.ASSIGN_RESERVATION_RULE:
        return isRuleFormInvalid;
      case BULK_ACTION_KEYS.REMOVE_RESERVATION_RULE:
        return !selectedRemoveRuleIds?.length;
      case BULK_ACTION_KEYS.ASSIGN_TAG:
        return isSimpleTagFlow ? !selectedTagIds?.length : isAssignFormInvalid;
      case BULK_ACTION_KEYS.REMOVE_TAG:
        return !selectedTagIds?.length;
      default:
        return true;
    }
  }, [
    activeActionKey,
    isAssignFormInvalid,
    isRuleFormInvalid,
    isSimpleTagFlow,
    selectedRemoveRuleIds,
    selectedTagIds,
  ]);

  const resolvedActions = useMemo(
    () =>
      (Array.isArray(actions) ? actions : [])
        .map((actionKey) => ALL_BULK_ACTIONS?.[actionKey])
        .filter((action) => action)
        .map((action) => ({
          ...action,
          description: action?.description?.replace(
            BULK_ACTION_ENTITY_TOKEN,
            resolvedEntityNoun,
          ),
        })),
    [actions, resolvedEntityNoun],
  );

  const removeTagOptions = useMemo(
    () => {
      if (isSimpleTagFlow) {
        const selectedRemoveTagIds = new Set(
          (selectedTagsForRemove || [])
            .map((t) => t?.id)
            .filter(Boolean),
        );

        return (assignTagOptions || []).filter((opt) =>
          selectedRemoveTagIds.has(opt?.value),
        );
      }

      return (
        selectedTagsForRemove?.map((t) => ({
          value: t?.id,
          label: t?.value && t?.itemKey ? `${t?.value} - ${t?.itemKey}` : '-',
        })) || []
      );
    },
    [selectedTagsForRemove, isSimpleTagFlow, assignTagOptions],
  );

  const [fetchReservationRules] = useLazyQuery(TOY_RESERVATION_RULES, {
    fetchPolicy: 'network-only',
    onCompleted: (response) => {
      const grouped = {};
      (response?.toyReservationRules?.data || [])?.forEach((rule) => {
        if (!grouped[rule?.type]) grouped[rule?.type] = [];
        grouped[rule?.type]?.push({ value: rule?.id, label: rule?.name });
      });
      setRulesByType(grouped);
      setIsRulesLoading(false);
    },
    onError: () => {
      setRulesByType({});
      setIsRulesLoading(false);
    },
  });

  const [attachToyReservationRule] = useMutation(ATTACH_TOY_RESERVATION_RULE, {
    onError: () => {},
  });

  const [fetchAssignTags] = useLazyQuery(DYNAMIC_CONFIG_ITEMS, {
    fetchPolicy: 'network-only',
    onCompleted: (response) => {
      const tags = response?.dynamicConfigItems?.data || [];
      setAssignTagOptions(
        tags?.map((t) => ({
          value: t?.id,
          label: t?.value && t?.itemKey ? `${t?.value} - ${t?.itemKey}` : '-',
        })) || [],
      );
    },
    onError: () => setAssignTagOptions([]),
  });

  const [bulkUpdateUserTags] = useMutation(BULK_UPDATE_USER_TAGS, {
    onError: () => {},
  });

  const [bulkUpdateOrderTags] = useMutation(BULK_UPDATE_ORDER_TAGS, {
    onError: () => {},
  });

  const [bulkUpdateLibraryInventoryTags] = useMutation(
    BULK_UPDATE_LIBRARY_INVENTORY_TAGS,
    {
      onError: () => {},
    },
  );

  const isEndUsersScope = scope === 'SYSTEM_USERS_END_USERS';
  const resolvedSupportsNestedSubmit =
    supportsNestedSubmit ?? isEndUsersScope;

  const clearAssignFieldErrors = () => {
    setNumberOfOrdersError('');
    setTagExpiryDateError('');
  };

  const resetNestedState = () => {
    setActiveActionKey(null);
    setSelectedTagIds([]);
    setErrorMessage('');
    setIsSubmitting(false);
    setRuleGroups(emptyRuleGroups());
    setSelectedRemoveRuleIds([]);
    setAssignTagApplicationType(ASSIGN_TAG_APPLICATION_TYPE.NUMBER_OF_ORDERS);
    setNumberOfOrders(null);
    setTagExpiryDate(null);
    clearAssignFieldErrors();
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    resetNestedState();
  };

  const handleBackToActions = () => {
    resetNestedState();
  };

  const handleCardClick = (action) => {
    onActionClick?.(action, selectedItemIds);

    if (!resolvedSupportsNestedSubmit || !action?.hasNestedUi) return;

    setActiveActionKey(action?.key);
    setSelectedTagIds([]);
    setErrorMessage('');
    setAssignTagApplicationType(ASSIGN_TAG_APPLICATION_TYPE.NUMBER_OF_ORDERS);
    setNumberOfOrders(null);
    setTagExpiryDate(null);
    clearAssignFieldErrors();

    if (action?.key === BULK_ACTION_KEYS.REMOVE_RESERVATION_RULE) {
      setSelectedRemoveRuleIds([]);
      return;
    }

    if (action?.key === BULK_ACTION_KEYS.ASSIGN_RESERVATION_RULE) {
      setRuleGroups(emptyRuleGroups());
      setIsRulesLoading(true);
      fetchReservationRules({
        variables: { filter: { skip: 0, limit: RULE_OPTIONS_LIMIT, isActive: true } },
      });
      return;
    }

    if (
      action?.key === BULK_ACTION_KEYS.ASSIGN_TAG ||
      (isSimpleTagFlow && action?.key === BULK_ACTION_KEYS.REMOVE_TAG)
    ) {
      fetchAssignTags({
        variables: {
          filter: {
            configType: DYNAMIC_CONFIG_TYPES?.TAGS?.value,
            configSubtypes: [tagConfigSubtype],
            skip: 0,
            limit: 200,
            search: null,
            isActive: true,
          },
          sort: { field: 'createdAt', order: SORT?.DESC },
        },
      });
    }
  };

  const handleAssignApplicationTypeChange = (value) => {
    setAssignTagApplicationType(value);
    clearAssignFieldErrors();
  };

  /**
   * Validates assign-tag scope and returns per-tag fields for the API.
   * @returns {{ orderCount: number | null, expirationDate: string | null } | null}
   */
  const buildValidatedAssignTagScope = () => {
    clearAssignFieldErrors();
    setErrorMessage('');

    if (!selectedTagIds?.length) {
      setErrorMessage('Please select at least one tag');
      return null;
    }

    if (assignTagApplicationType === ASSIGN_TAG_APPLICATION_TYPE.NUMBER_OF_ORDERS) {
      if (numberOfOrders === null || numberOfOrders === undefined) {
        setNumberOfOrdersError('Please enter a number of orders');
        return null;
      }
      const num = Number(numberOfOrders);
      if (!Number.isInteger(num) || num <= 0) {
        setNumberOfOrdersError(
          num === 0
            ? 'Value must be greater than 0'
            : 'Enter a whole number greater than 0',
        );
        return null;
      }
      return { orderCount: num, expirationDate: null };
    }

    if (!tagExpiryDate || !dayjs.isDayjs(tagExpiryDate)) {
      setTagExpiryDateError('Please select a tag expiry date');
      return null;
    }
    if (tagExpiryDate.isBefore(dayjs(), 'day')) {
      setTagExpiryDateError('Date must be today or in the future');
      return null;
    }
    return {
      orderCount: null,
      expirationDate: tagExpiryDate.format('YYYY-MM-DD'),
    };
  };

  const mapIdsToUserTagInputs = (tagIds, { orderCount, expirationDate }) =>
    (tagIds || []).map((tagId) => ({
      tagId,
      orderCount: orderCount ?? null,
      expirationDate: expirationDate ?? null,
    }));

  const submitReservationRules = async ({ data, successMessage, errorText }) => {
    setIsSubmitting(true);
    setErrorMessage('');
    try {
      const response = await attachToyReservationRule({ variables: { data } });

      if (!response?.data?.attachToyReservationRule) {
        throw new Error(errorText);
      }

      message?.success(successMessage);
      handleCloseDrawer();
      onClearSelection?.();
      onSuccess?.();
    } catch (e) {
      setErrorMessage(
        e?.graphQLErrors?.[0]?.message ||
          e?.networkError?.result?.errors?.[0]?.message ||
          e?.message ||
          errorText,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (!resolvedSupportsNestedSubmit) return;

    if (activeActionKey === BULK_ACTION_KEYS.ASSIGN_RESERVATION_RULE) {
      await submitReservationRules({
        data: buildAddRulesInput(selectedItemIds, ruleGroups),
        successMessage: `Reservation rules assigned to ${selectedCount} ${resolvedEntityNoun}`,
        errorText: 'Failed to assign reservation rules',
      });
      return;
    }

    if (activeActionKey === BULK_ACTION_KEYS.REMOVE_RESERVATION_RULE) {
      await submitReservationRules({
        data: buildRemoveRulesInput(selectedItemIds, selectedRemoveRuleIds),
        successMessage: `Reservation rules removed from ${selectedCount} ${resolvedEntityNoun}`,
        errorText: 'Failed to remove reservation rules',
      });
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');
    clearAssignFieldErrors();

    try {
      const isAddOperation = activeActionKey === BULK_ACTION_KEYS.ASSIGN_TAG;

      let data;

      if (isSimpleTagFlow) {
        if (!selectedTagIds?.length) {
          setErrorMessage('Please select at least one tag');
          setIsSubmitting(false);
          return;
        }

        if (resolvedTagEntity === BULK_TAG_ENTITY.LIBRARY_INVENTORIES) {
          await bulkUpdateLibraryInventoryTags({
            variables: {
              data: {
                isAddOperation,
                tags: selectedTagIds,
                libraryInventories: selectedItemIds,
              },
            },
          });
        } else {
          const ordersIds =
            orderTagTargetField === 'orders' ? selectedItemIds : [];
          const returnOrdersIds =
            orderTagTargetField === 'returnOrders' ? selectedItemIds : [];

          data = {
            isAddOperation,
            orderTags: selectedTagIds,
            orders: ordersIds,
            returnOrders: returnOrdersIds,
          };

          await bulkUpdateOrderTags({
            variables: { data },
          });
        }

        handleCloseDrawer();
        onClearSelection?.();
        onSuccess?.();
        return;
      }

      if (isAddOperation) {
        const assignScope = buildValidatedAssignTagScope();
        if (!assignScope) {
          setIsSubmitting(false);
          return;
        }
        data = {
          isAddOperation: true,
          userIds: selectedItemIds,
          userTags: mapIdsToUserTagInputs(selectedTagIds, assignScope),
        };
      } else {
        if (!selectedTagIds?.length) {
          setErrorMessage('Please select at least one tag');
          setIsSubmitting(false);
          return;
        }
        data = {
          isAddOperation: false,
          userIds: selectedItemIds,
          userTags: mapIdsToUserTagInputs(selectedTagIds, {
            orderCount: null,
            expirationDate: null,
          }),
        };
      }

      await bulkUpdateUserTags({
        variables: { data },
      });

      handleCloseDrawer();
      onClearSelection?.();
      onSuccess?.();
    } catch (e) {
      setErrorMessage(
        e?.graphQLErrors?.[0]?.message ||
          e?.networkError?.result?.errors?.[0]?.message ||
          e?.message ||
          'Failed to update tags',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderNestedActionContent = () => {
    if (!resolvedSupportsNestedSubmit || !activeActionKey) return null;

    switch (activeActionKey) {
      case BULK_ACTION_KEYS.ASSIGN_TAG:
        if (isSimpleTagFlow) {
          return (
            <AssignSimpleTagDrawerContent
              assignTagOptions={assignTagOptions}
              selectedTagIds={selectedTagIds}
              onSelectedTagIdsChange={setSelectedTagIds}
            />
          );
        }

        return (
          <AssignTagDrawerContent
            assignTagOptions={assignTagOptions}
            selectedTagIds={selectedTagIds}
            onSelectedTagIdsChange={setSelectedTagIds}
            assignTagApplicationType={assignTagApplicationType}
            onAssignTagApplicationTypeChange={handleAssignApplicationTypeChange}
            numberOfOrders={numberOfOrders}
            onNumberOfOrdersChange={(v) => {
              setNumberOfOrders(v);
              if (v === null || v === undefined) {
                setNumberOfOrdersError('');
                return;
              }
              const n = Number(v);
              if (!Number.isInteger(n) || n <= 0) {
                setNumberOfOrdersError(
                  n === 0
                    ? 'Value must be greater than 0'
                    : 'Enter a whole number greater than 0',
                );
              } else {
                setNumberOfOrdersError('');
              }
            }}
            tagExpiryDate={tagExpiryDate}
            onTagExpiryDateChange={(v) => {
              setTagExpiryDate(v);
              if (tagExpiryDateError) setTagExpiryDateError('');
            }}
            numberOfOrdersError={numberOfOrdersError}
            tagExpiryDateError={tagExpiryDateError}
          />
        );
      case BULK_ACTION_KEYS.ASSIGN_RESERVATION_RULE:
        return (
          <AssignReservationRuleDrawerContent
            groups={ruleGroups}
            rulesByType={rulesByType}
            isRulesLoading={isRulesLoading}
            onGroupsChange={setRuleGroups}
            selectedCount={selectedCount}
            entityNoun={resolvedEntityNoun}
          />
        );
      case BULK_ACTION_KEYS.REMOVE_RESERVATION_RULE:
        return (
          <RemoveReservationRuleDrawerContent
            rules={selectedRulesForRemove}
            selectedRuleIds={selectedRemoveRuleIds}
            onSelectedRuleIdsChange={setSelectedRemoveRuleIds}
            selectedCount={selectedCount}
            entityNoun={resolvedEntityNoun}
          />
        );
      case BULK_ACTION_KEYS.REMOVE_TAG:
        return (
          <RemoveTagDrawerContent
            removeTagOptions={removeTagOptions}
            selectedTagIds={selectedTagIds}
            onSelectedTagIdsChange={setSelectedTagIds}
          />
        );
      default:
        return null;
    }
  };

  if (resolvedActions?.length === 0) return null;

  const isBulkInactive = selectedCount === 0;

  return (
    <>
      <Button
        type="primary"
        className={`common-button bulk-action-button ${className}${isBulkInactive ? ' bulk-action-button--awaiting-selection' : ''}`.trim()}
        aria-disabled={isBulkInactive}
        onClick={() => {
          if (isBulkInactive) return;
          setIsDrawerOpen(true);
        }}
      >
        {selectedCount > 0 ? `${label} (${selectedCount})` : label}
      </Button>

      <Drawer
        title={drawerTitle}
        placement="right"
        open={isDrawerOpen}
        onClose={handleCloseDrawer}
        width="80%"
        className="custom-drawer bulk-action-drawer"
        destroyOnClose
        footer={
          resolvedSupportsNestedSubmit && activeActionKey ? (
            <div className="bulk-assign-tag-actions">
              <Button onClick={handleCloseDrawer}>Cancel</Button>
              <Button
                type="primary"
                loading={isSubmitting}
                disabled={nestedSubmitDisabled}
                onClick={handleSubmit}
              >
                Submit
              </Button>
            </div>
          ) : null
        }
      >
        {errorMessage ? (
          <Alert className="mb-16" type="error" showIcon message={errorMessage} />
        ) : null}

        {!activeActionKey ? (
          <ActionCardsGrid
            actions={resolvedActions}
            onCardClick={handleCardClick}
          />
        ) : null}

        {resolvedSupportsNestedSubmit && activeActionKey ? (
          <>
            <Button
              type="link"
              icon={<LeftOutlined />}
              className="p-0 mb-12 bulk-action-back-button"
              onClick={handleBackToActions}
            >
              Back
            </Button>
            {renderNestedActionContent()}
          </>
        ) : null}
      </Drawer>
    </>
  );
};

export default BulkActionButton;
